/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { useEffect, useState } from "react";
import fetchWithAuth from "layouts/tables/data/api";
import refreshTokensTableData from "layouts/tables/data/refreshTokensTableData";
import appSettings from "appsettings.json";

function RefreshTokens() {
  const [refreshTokens, setRefreshTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [openDeleteAllConfirm, setOpenDeleteAllConfirm] = useState(false);
  const [error, setError] = useState(null);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.$values)) return value.$values;
    return [];
  };

  const fetchRefreshTokens = async () => {
    setLoading(true);
    setError(null);

    const url = `${appSettings.baseUrl}${
      appSettings.endpoints.getAllRefreshTokens
    }?_ts=${Date.now()}`;
    try {
      const response = await fetchWithAuth(url, {
        method: "GET",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("An error occurred while fetching refresh tokens.");
      }

      const data = await response.json();
      setRefreshTokens(normalizeArray(data));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefreshTokens();
  }, []);

  const handleDeleteExpiredTokens = async () => {
    setDeleting(true);
    setError(null);

    const url = `${appSettings.baseUrl}${appSettings.endpoints.deleteExpiredRefreshTokens}`;
    try {
      const response = await fetchWithAuth(url, {
        method: "POST",
      });

      if (!response.ok) {
        let errorMessage = "An error occurred while deleting expired refresh tokens.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch (e) {
          // Keep default message when response is not JSON.
        }
        throw new Error(errorMessage);
      }

      setOpenDeleteConfirm(false);
      await fetchRefreshTokens();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAllTokens = async () => {
    setDeletingAll(true);
    setError(null);

    const url = `${appSettings.baseUrl}${appSettings.endpoints.deleteAllRefreshTokens}`;
    try {
      const response = await fetchWithAuth(url, {
        method: "POST",
      });

      if (!response.ok) {
        let errorMessage = "An error occurred while deleting all refresh tokens.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch (e) {
          // Keep default message when response is not JSON.
        }
        throw new Error(errorMessage);
      }

      setOpenDeleteAllConfirm(false);
      await fetchRefreshTokens();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingAll(false);
    }
  };

  const handleOpenDeleteConfirm = () => {
    setOpenDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    if (deleting) return;
    setOpenDeleteConfirm(false);
  };

  const handleOpenDeleteAllConfirm = () => {
    setOpenDeleteAllConfirm(true);
  };

  const handleCloseDeleteAllConfirm = () => {
    if (deletingAll) return;
    setOpenDeleteAllConfirm(false);
  };

  const { columns, rows } = refreshTokensTableData(refreshTokens);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Refresh Tokens
                </MDTypography>
                <MDBox display="flex" gap={1}>
                  <MDButton
                    variant="gradient"
                    color="success"
                    size="small"
                    onClick={handleOpenDeleteConfirm}
                    disabled={deleting || deletingAll || loading}
                  >
                    {deleting ? "Removing..." : "Remove Expired"}
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="error"
                    size="small"
                    onClick={handleOpenDeleteAllConfirm}
                    disabled={deleting || deletingAll || loading}
                  >
                    {deletingAll ? "Removing..." : "Remove All"}
                  </MDButton>
                </MDBox>
              </MDBox>
              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={10}>
                    <CircularProgress color="success" />
                  </MDBox>
                ) : error ? (
                  <MDBox p={3}>
                    <MDTypography variant="button" color="error">
                      Error: {error}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    canSearch
                    isSorted={true}
                    entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20] }}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog open={openDeleteConfirm} onClose={handleCloseDeleteConfirm} fullWidth maxWidth="xs">
        <DialogTitle>Delete Expired Tokens</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to remove all expired/deactivated refresh tokens from database?
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={handleCloseDeleteConfirm} color="secondary" disabled={deleting}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleDeleteExpiredTokens}
            variant="gradient"
            color="error"
            disabled={deleting}
          >
            {deleting ? "Removing..." : "Confirm"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteAllConfirm}
        onClose={handleCloseDeleteAllConfirm}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete All Tokens</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to remove all refresh tokens from database?
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={handleCloseDeleteAllConfirm} color="secondary" disabled={deletingAll}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleDeleteAllTokens}
            variant="gradient"
            color="error"
            disabled={deletingAll}
          >
            {deletingAll ? "Removing..." : "Confirm"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default RefreshTokens;
